/* eslint-disable style/quote-props */
import type { DragEndEvent } from '@dnd-kit/core';

import type { DraggableListQuestion } from '@shared/domain/form';
import {
  closestCenter,
  DndContext,

  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Delete, DragIndicator } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, TextField } from '@mui/material';
import { motion } from 'framer-motion';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { MOBILE_BREAKPOINT } from '@shared/styles/Breakpoints';

export interface Item { id: string; text: string }

interface Props {
  question: DraggableListQuestion;
  formFields: Record<string, any>;
  helperText?: string;
  handleFormFieldChange: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  >;
  isConditional?: boolean;
}

// Gentle settle, no scale pop — SCSS glow ring carries the attention.
const CONDITIONAL_ENTRANCE = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const DEFAULT_ENTRANCE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

interface SortableItemProps {
  id: string;
  item: Item;
  index: number;
  onRemove: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, item, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '50%',
        userSelect: 'none',
        padding: '8px',
        marginBottom: '4px',
        background: isDragging
          ? 'rgba(0,123,255,0.2)'
          : index % 2 === 0
            ? '#e3f2fd'
            : '#fcf6e4ff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        opacity: isDragging ? 0.5 : 1,
      }}
      {...attributes}
    >
      <Box
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: '#666',
          marginRight: '8px',
          '&:active': {
            cursor: 'grabbing',
          },
          '&:hover': {
            color: '#4a3f33',
          },
        }}
      >
        <DragIndicator fontSize="small" />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
        }}
      >
        {index + 1}
        .
        {' '}
        {item.text}
      </Box>
      <Box
        component={IconButton}
        size="small"
        onClick={onRemove}
        aria-label={`Remove ${item.text}`}
        sx={{
          marginLeft: '8px',
          padding: '4px',
          color: '#999',
          '&:hover': {
            color: '#f44336',
            backgroundColor: 'rgba(244, 67, 54, 0.04)',
          },
        }}
      >
        <Delete fontSize="small" />
      </Box>
    </Box>
  );
};

const ReviewDraggableListQuestion: React.FC<Props> = ({
  question,
  formFields,
  helperText,
  handleFormFieldChange,
  isConditional = false,
}) => {
  const [items, setItems] = useState<Item[]>([]);
  const [current, setCurrent] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const init: Item[] = (formFields[question.name] || []).map(
      (text: string, idx: number) => ({
        id: `item-${idx}`,
        text,
      }),
    );
    setItems(init);
  }, [formFields, question.name]);

  const pushUp = useCallback(
    (newItems: Item[]) => {
      const plain = newItems.map(i => i.text);
      if (!_.isEqual(formFields[question.name], plain)) {
        handleFormFieldChange(prev => ({
          ...prev,
          [question.name]: plain,
        }));
      }
    },
    [formFields, question.name, handleFormFieldChange],
  );

  const handleAdd = () => {
    if (!current.trim())
      return;
    const next: Item = { id: `item-${items.length}`, text: current };
    const nextList = [...items, next];
    setItems(nextList);
    pushUp(nextList);
    setCurrent('');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);

    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    setItems(reorderedItems);
    pushUp(reorderedItems);
  };

  const handleRemove = (itemId: string) => {
    const filteredItems = items.filter(item => item.id !== itemId);
    setItems(filteredItems);
    pushUp(filteredItems);
  };

  const motionProps = isConditional ? CONDITIONAL_ENTRANCE : DEFAULT_ENTRANCE;
  const className = [
    'review-question',
    isConditional && 'review-question--conditional',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.section
      className={className}
      data-testid={question.name}
      {...motionProps}
    >
      <Grid size={{ sm: 6, xs: 12 }}>
        <h3 className="review-question__prompt">{question.question}</h3>

        <Box
          sx={{
            display: 'flex',
            gap: 'var(--spacing-md)',
            marginBottom: '14px',
            marginTop: '10px',
          }}
        >
          <TextField
            data-testid={`${question.name}-input`}
            value={current}
            onChange={e => setCurrent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Return') {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder={question.placeholder}
            size="small"
            required
            sx={{
              width: '40%',
              [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
                marginTop: 'var(--spacing-xs)',
                width: '100%',
              },
            }}
          />
          <Button
            variant="outlined"
            disabled={!current.trim()}
            onClick={handleAdd}
            data-testid={`${question.name}-add-button`}
            sx={{ flexShrink: 0 }}
          >
            Add
          </Button>
        </Box>
        {helperText && <p className="review-question__helper">{helperText}</p>}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
            <div>
              {items.map((item, idx) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  item={item}
                  index={idx}
                  onRemove={() => handleRemove(item.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

      </Grid>
    </motion.section>
  );
};

export default ReviewDraggableListQuestion;
